import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Bird, Cat, ChevronRight, Dog, Fish, HeartHandshake, MapPin, Rabbit, Scissors, ShieldCheck, ShoppingBag, Squirrel, Truck, UserRound } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useLocation, openLocationPicker } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";
import { PET_TYPE_OF, SPECIES_LABEL } from "@/lib/pets";
import { ForYouStrip } from "@/components/BuyAgain";
import ProductTile from "@/components/ProductTile";
import { BannerSlider } from "@/components/Sliders";
import { useSiteSlides } from "@/components/SiteSlides";
import { CARE_URL, COMMUNITY_URL } from "@/lib/sites";
import { useCategories } from "@/components/MegaNav";
import { FAQ } from "@/lib/policies";
import { useSeo } from "@/lib/seo";

/** pawbazaar.store: the front door to everything PawBazaar does. Three equal
 *  doors (Shop, Care, Community) with live numbers for the visitor's place,
 *  then a row from each: products, care services, pets and NGO needs. The
 *  product-first page is /shop. */

// The questions people ask first (same answers as the Help centre).
const HOME_FAQ = [
  ...FAQ[0][1].filter(([q]) => /fast|cancel/i.test(q)),
  ["Which areas do you deliver to?", "We deliver from shops near you. Set your location or pincode at the top of the page to see the shops that deliver there."],
  ["How do I book grooming or a vet visit?", "Open Care, pick the service and a provider near you, choose a free time and pay online. Many providers also offer pickup and drop."],
  ["How does adoption work?", "Open Community, pick a pet and send the NGO a request. The NGO calls you, and the pet comes home after their checks and a handover."],
  ...FAQ[1][1].filter(([q]) => /pay\?|refund/i.test(q)),
];
const PET_TILES = [["Dogs", Dog], ["Cats", Cat], ["Birds", Bird], ["Fish", Fish], ["Rabbits", Rabbit], ["Small Pets", Squirrel]];

/** Signed-in customers: products picked for their saved pets (one row per kind,
 *  up to two), or an "Add your pet" prompt when none are saved. */
function FreshPicks({ list }) {
  const { user } = useAuth();
  const [mine, setMine] = useState(null);       // [{kind, names, items}]
  useEffect(() => {
    if (user?.role !== "customer") return;
    let live = true;
    api.get("/customer/pets").then(async ({ data }) => {
      const byKind = {};
      (data || []).forEach((p) => { if (PET_TYPE_OF[p.species]) (byKind[p.species] = byKind[p.species] || []).push(p.name); });
      const kinds = Object.keys(byKind).slice(0, 2);
      const rows = await Promise.all(kinds.map((k) => api.get("/products", { params: { ...list, pet_type: PET_TYPE_OF[k], sort: "popular", per_page: 6 } })
        .then(({ data: d }) => ({ kind: k, names: byKind[k], items: d.items || [] }), () => ({ kind: k, names: byKind[k], items: [] }))));
      if (live) setMine(rows);
    }, () => live && setMine([]));
    return () => { live = false; };
  }, [user, list]);
  if (user?.role !== "customer" || mine === null) return null;
  if (!mine.length) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8" data-testid="home-add-pet">
        <Link to="/account?tab=pets" className="flex items-center gap-4 rounded-2xl bg-teal/10 hover:bg-teal/15 transition-colors p-4">
          <Dog className="h-8 w-8 text-teal shrink-0" aria-hidden />
          <div className="flex-1"><div className="font-semibold">Add your pet</div>
            <div className="text-sm text-stone-600">Get food, treats and care picked for them. It takes 10 seconds.</div></div>
          <ChevronRight className="h-5 w-5 text-teal" aria-hidden />
        </Link>
      </section>
    );
  }
  const shown = mine.filter((r) => r.items.length);
  if (!shown.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 space-y-6" data-testid="home-pet-picks">
      {shown.map((r) => (
        <div key={r.kind}>
          <div className="flex items-end justify-between gap-3 mb-3">
            <h2 className="font-heading text-xl sm:text-2xl font-semibold">Fresh picks for {r.names.slice(0, 2).join(" and ")}</h2>
            <Link to={`/shop?pet_type=${encodeURIComponent(PET_TYPE_OF[r.kind])}`} className="text-sm font-semibold text-teal hover:underline shrink-0">All {SPECIES_LABEL[r.kind].toLowerCase()} products</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {r.items.slice(0, 4).map((p) => <ProductTile key={p.id} p={p} testid={`home-pick-${p.id}`} />)}
          </div>
        </div>
      ))}
    </section>
  );
}

const HOME_TITLE = "Pet supplies, grooming, vets and adoption near you";
const HOME_DESCRIPTION = "Order pet food and supplies from verified shops near you, book grooming, vet visits and training, and adopt or help pets with local NGOs. One account for everything your pet needs.";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default function Landing() {
  const { locationParams, locationLabel, looking } = useLocation();
  const [shop, setShop] = useState(null);       // {items, total}
  const [offers, setOffers] = useState([]);
  const [care, setCare] = useState(null);       // {items, providers}
  const [pets, setPets] = useState(null);
  const [needs, setNeeds] = useState([]);
  const [slides] = useSiteSlides("home", "shop", mediaUrl);

  useSeo({
    title: HOME_TITLE, description: HOME_DESCRIPTION, path: "/",
    jsonLd: { "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: HOME_FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
  });

  useEffect(() => {
    const point = locationParams.lat != null ? { lat: locationParams.lat, lng: locationParams.lng } : {};
    const list = { ...locationParams, group: "family", page: 1, per_page: 8 };
    api.get("/products", { params: { ...list, sort: "popular" } }).then(({ data }) => setShop(data), () => setShop({ items: [], total: 0 }));
    api.get("/products", { params: { ...list, on_offer: true, sort: "discount" } }).then(({ data }) => setOffers(data.items || []), () => setOffers([]));
    api.get("/care/service-types", { params: { ...point, per_page: 4 } }).then(({ data }) => setCare(data), () => setCare({ items: [], providers: 0 }));
    api.get("/community/pets", { params: point }).then(({ data }) => setPets(Array.isArray(data) ? data : []), () => setPets([]));
    api.get("/community/needs", { params: point }).then(({ data }) => setNeeds(Array.isArray(data) ? data.slice(0, 2) : []), () => setNeeds([]));
  }, [locationParams]);

  const place = looking ? "Mumbai" : locationLabel;
  const picksList = useMemo(() => ({ ...locationParams, group: "family", page: 1 }), [locationParams]);
  const shopLine = !shop ? "Food, treats, toys and medicines from shops near you."
    : shop.total ? `${count(shop.total, "product", "products")} from shops near ${place}. Delivered today.`
      : `No shop delivers to ${place} yet. Try another place.`;
  const careLine = !care ? "Grooming, vets, vaccinations and training. Pickup and drop available."
    : care.providers ? `${count(care.providers, "provider", "providers")} near you for grooming, vets and training. Pick a time and pay online.`
      : "Grooming, vets, vaccinations and training. Pick a time and pay online.";
  const cats = useCategories();
  const commLine = pets?.length ? `${count(pets.length, "pet is", "pets are")} waiting for a home. Adopt, donate or join a drive.`
    : "Adopt a pet, donate to verified NGOs and join drives near you.";

  // "Dog food": the pet plus the category whose name says food (a main category first), not a word
  // search, which misses foods without "dog" in the name. A word search only if there is no such category.
  const petLink = (pet, rx, words) => {
    const c = cats.find((x) => !x.parent_id && rx.test(x.name)) || cats.find((x) => rx.test(x.name));
    return c ? `/shop?pet_type=${pet}&category=${encodeURIComponent(c.name)}` : `/shop?q=${encodeURIComponent(words)}`;
  };

  return (
    <div>
      {/* -------- The three doors -------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10" data-testid="home-hero">
        <button type="button" onClick={() => openLocationPicker()} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold uppercase tracking-[0.14em] text-terracotta hover:underline" data-testid="hero-place">
          <MapPin className="h-4 w-4" /> {looking ? "All of Mumbai · choose your area" : place}
        </button>
        <h1 className="font-heading text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.08] mt-2 text-stoneWarm max-w-3xl">Everything your pet needs, near you</h1>
        <p className="mt-3 text-base sm:text-lg text-stone-600 max-w-2xl">Order supplies from local shops, book grooming or a vet, and help pets waiting for a home.</p>
        <div className="mt-6 grid gap-3 sm:gap-4 md:grid-cols-3">
          <Door to="/shop" tone="bg-[#FFF1DC]" title="Shop" line={shopLine} cta="Start shopping" ctaCls="text-stoneWarm" Icon={ShoppingBag} testid="door-shop"
            chips={[["Dog food", petLink("Dogs", /food/i, "dog food")], ["Cat litter", petLink("Cats", /litter/i, "cat litter")], ["Offers", "/shop?offers=1"]]} />
          <Door href={`${CARE_URL}/services`} tone="bg-[#E3F4F2]" title="Care" line={careLine} cta="Book care" ctaCls="text-[#0F6E68]" Icon={Scissors} testid="door-care"
            chips={[["Grooming", `${CARE_URL}/services?group=grooming`], ["Vet visit", `${CARE_URL}/services?group=vet`], ["Training", `${CARE_URL}/services?group=training`]]} />
          <Door href={COMMUNITY_URL} tone="bg-[#EAF1DF]" title="Community" line={commLine} cta="Join in" ctaCls="text-[#3F6B2A]" Icon={HeartHandshake} testid="door-community"
            chips={[["Adopt", `${COMMUNITY_URL}/adopt`], ["Donate", `${COMMUNITY_URL}/campaigns`], ["Drives", `${COMMUNITY_URL}/drives`]]} />
        </div>
      </section>

      {slides.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8" data-testid="home-slides"><BannerSlider slides={slides} /></section>
      )}

      {/* Vaccines due, food running low, the last order to buy again (signed in). */}
      <ForYouStrip />
      <FreshPicks list={picksList} />

      {/* -------- Shop -------- */}
      <Row title="Shop for your pet" more={{ to: "/shop", label: "See all products" }} testid="home-shop">
        <ul className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
          {PET_TILES.map(([name, Icon]) => (
            <li key={name}>
              <Link to={`/shop?pet_type=${encodeURIComponent(name)}`} data-testid={`home-pet-type-${name.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-stone-200 bg-white py-3 hover:border-marigold">
                <Icon className="h-7 w-7 text-marigold" strokeWidth={1.75} aria-hidden />
                <span className="text-sm font-semibold">{name === "Small Pets" ? "Small pets" : name}</span>
              </Link>
            </li>
          ))}
        </ul>
        {!shop ? <Skeleton n={4} /> : shop.items.length ? (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="home-products">
            {shop.items.map((p) => <ProductTile key={p.id} p={p} testid={`home-product-${p.id}`} />)}
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed border-stone-300 bg-white p-5 text-sm text-stone-600" data-testid="home-no-shop">
            No shop delivers to <b>{place}</b> yet.{" "}
            <button type="button" onClick={() => openLocationPicker()} className="font-semibold text-teal hover:underline">Try another place</button>, or book care and meet pets near you below.
          </div>
        )}
      </Row>

      {/* -------- Care -------- */}
      {care?.items?.length > 0 && (
        <Row title="Care near you" more={{ href: `${CARE_URL}/services`, label: "See all services" }} testid="home-care">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {care.items.map((s) => (
              <a key={s.key} href={`${CARE_URL}/service/${s.key}`} className="group rounded-2xl overflow-hidden border border-stone-200 bg-white hover:shadow-lg transition-shadow" data-testid={`home-service-${s.key}`}>
                <div className="aspect-[4/3] bg-[#E3F4F2] overflow-hidden">
                  {s.image_url ? <img src={mediaUrl(s.image_url)} alt="" loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="h-full w-full flex items-center justify-center"><Scissors className="h-10 w-10 text-[#0F6E68]/50" aria-hidden /></div>}
                </div>
                <div className="p-3">
                  <div className="font-semibold leading-snug">{s.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{count(s.providers, "provider", "providers")}{s.pickup ? " · pickup available" : s.home_visit ? " · home visit" : ""}</div>
                  <div className="text-sm font-bold mt-1">from {rupees(s.from_price)}</div>
                </div>
              </a>
            ))}
          </div>
        </Row>
      )}

      {/* -------- Community -------- */}
      {(pets?.length > 0 || needs.length > 0) && (
        <Row title="Help a pet today" more={{ href: COMMUNITY_URL, label: "See all" }} testid="home-community">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {(pets || []).slice(0, needs.length ? 2 : 4).map((p) => (
              <a key={p.id} href={`${COMMUNITY_URL}/adopt/${p.id}`} className="group rounded-2xl overflow-hidden border border-stone-200 bg-white hover:shadow-lg transition-shadow" data-testid={`home-pet-${p.id}`}>
                <div className="aspect-[4/3] bg-[#EAF1DF] overflow-hidden">
                  {p.photo_urls?.[0] ? <img src={mediaUrl(p.photo_urls[0])} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                    : <div className="h-full w-full flex items-center justify-center"><Dog className="h-10 w-10 text-[#3F6B2A]/50" aria-hidden /></div>}
                </div>
                <div className="p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#3F6B2A]">Adopt</div>
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-stone-500 truncate">{p.ngo?.name}{p.vaccinated ? " · vaccinated" : ""}</div>
                </div>
              </a>
            ))}
            {needs.map((x) => (
              <a key={x.id} href={`${COMMUNITY_URL}/campaigns/${x.id}`} className="rounded-2xl border border-stone-200 bg-white p-4 flex flex-col hover:shadow-lg transition-shadow" data-testid={`home-need-${x.id}`}>
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#3F6B2A]">NGO need</div>
                <div className="font-semibold mt-1">{x.title}</div>
                <div className="text-xs text-stone-500 truncate">{x.ngo?.name}</div>
                <div className="mt-auto pt-3">
                  <div className="h-2 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-[#3F6B2A]" style={{ width: `${Math.min(100, (100 * (x.quantity_needed - x.quantity_remaining)) / Math.max(1, x.quantity_needed))}%` }} /></div>
                  <div className="text-xs text-stone-600 mt-1">{x.quantity_remaining} {x.unit} still needed</div>
                </div>
              </a>
            ))}
          </div>
        </Row>
      )}

      {/* -------- Offers -------- */}
      {offers.length > 0 && (
        <Row title="Offers from shops near you" more={{ to: "/shop?offers=1", label: "See all offers" }} testid="home-offers">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {offers.slice(0, 4).map((p) => <ProductTile key={p.id} p={p} testid={`home-offer-${p.id}`} />)}
          </div>
        </Row>
      )}

      {/* -------- Why PawBazaar and questions -------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-2 gap-10" aria-labelledby="about-title" data-testid="home-about">
        <div>
          <h2 id="about-title" className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Why PawBazaar</h2>
          <p className="mt-3 text-stone-700 leading-relaxed max-w-xl">PawBazaar brings your neighbourhood's pet shops, groomers, vets, trainers and animal NGOs together, so everything your pet needs is a few taps away.</p>
          <ul className="mt-5 grid sm:grid-cols-3 gap-3 text-sm">
            {[[ShieldCheck, "Verified businesses", "Every shop, provider and NGO is checked before it goes live."], [Truck, "Same-day delivery", "From shops in your area, with live tracking."],
              [UserRound, "One account", "Orders, bookings and your pets in one place."]].map(([Icon, t, d]) => (
              <li key={t} className="rounded-2xl bg-white border border-stone-200 p-4"><Icon className="h-5 w-5 text-teal" aria-hidden /><span className="block font-semibold mt-2">{t}</span><span className="block text-stone-600 mt-0.5">{d}</span></li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Questions</h2>
          <div className="mt-4 divide-y divide-stone-200 rounded-2xl border border-stone-200 bg-white" data-testid="home-faq">
            {HOME_FAQ.map(([q, a]) => (
              <details key={q} className="group p-4">
                <summary className="cursor-pointer font-semibold list-none flex justify-between gap-3">{q}<ChevronRight className="h-4 w-4 shrink-0 mt-1 transition-transform group-open:rotate-90" aria-hidden /></summary>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
          <Link to="/help-centre" className="inline-block mt-3 text-sm font-semibold text-teal hover:underline">More answers in the Help centre</Link>
        </div>
      </section>
    </div>
  );
}

/** One of the three big doors: a site, a live line about it, quick links. */
function Door({ to, href, tone, title, line, cta, ctaCls, chips, Icon, testid }) {
  const body = (
    <>
      <span className="flex items-center justify-between">
        <span className="font-heading text-2xl sm:text-3xl font-semibold">{title}</span>
        <Icon className="h-6 w-6 opacity-60" aria-hidden />
      </span>
      <span className="text-sm sm:text-[15px] text-stone-700 flex-1">{line}</span>
      <span className={`inline-flex items-center gap-1 font-bold ${ctaCls}`}>{cta} <ArrowRight className="h-4 w-4" /></span>
    </>
  );
  const cls = `rounded-3xl ${tone} p-5 sm:p-6 flex flex-col gap-3 min-h-[11rem] sm:min-h-[13rem] hover:shadow-md transition-shadow`;
  return (
    <div className="flex flex-col gap-2" data-testid={testid}>
      {to ? <Link to={to} className={cls}>{body}</Link> : <a href={href} className={cls}>{body}</a>}
      <div className="flex flex-wrap gap-1.5 px-1">
        {chips.map(([label, link]) => (link.startsWith("http")
          ? <a key={label} href={link} className="rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-semibold text-stone-700 hover:border-stoneWarm">{label}</a>
          : <Link key={label} to={link} className="rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-semibold text-stone-700 hover:border-stoneWarm">{label}</Link>))}
      </div>
    </div>
  );
}

function Row({ title, more, testid, children }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h2>
        {more.to ? <Link to={more.to} className="shrink-0 text-sm font-semibold text-teal hover:underline">{more.label}</Link>
          : <a href={more.href} className="shrink-0 text-sm font-semibold text-teal hover:underline">{more.label}</a>}
      </div>
      {children}
    </section>
  );
}

function Skeleton({ n }) {
  return (
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
      {Array.from({ length: n }, (_, i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-stone-100 animate-pulse" />)}
    </div>
  );
}
